<template>
  <validation-observer v-slot="{ handleSubmit, meta }">
    <ld-modal-content :title="selectedForm?.name ? selectedForm.name : 'Select Action Template'" :class="{'form-selected': selectedFormId, 'form-selection': !selectedFormId}" class="form-modal form-fill-container">
      <template #header-left-buttons v-if="selectedFormId">
        <button class="button is-white" @click="submission ? $emit('close') : (selectedFormId = null, selectedForm = {})">
          <span class="icon">
            <i class="mdi mdi-arrow-left"></i>
          </span>
          <span>{{ submission ? 'Back' : 'Action Templates' }}</span>
        </button>
      </template>
      <template #subtitle v-if="selectedForm.description">
        <span v-tooltip="{ theme: 'lookdeep-global', content: selectedForm.description, placement: 'bottom', disabled: !isTruncated(selectedForm.description) }">{{ truncateDescription(selectedForm.description) }}</span>
      </template>
      <template #header-right-buttons>
        <button class="button is-white" v-if="submission?.is_aimee" @click="openAimeeConversation">
          <span class="icon">
            <img src="@/assets/icons/ai-summary.svg" class="summary-icon"/>
          </span>
          <span>Aimee Detail</span>
        </button>
        <button class="button is-white" v-if="submission" @click="toggleHistory">
          <span class="icon">
            <i :class="['mdi', showHistory ? 'mdi-form-textbox' : 'mdi-history']"></i>
          </span>
          <span>{{ showHistory ? 'Action' : 'History' }}</span>
        </button>
      </template>
      <template #default>
        <div class="choose-form" v-if="!selectedFormId">
          <b-select v-model="selectedFormId" @input="formSelected" expanded>
            <option :value="null">Select an Action Template</option>
            <option v-for="form in forms" :key="form.id" :value="form.id">{{ form.name }}</option>
          </b-select>
        </div>
        <template v-if="selectedFormId">
          <div class="form-content">
            <div v-if="validationErrors.length > 0" class="validation-errors">
              <div class="notification is-warning">
                <p class="has-text-weight-medium mb-2">This submission needs review:</p>
                <ul>
                  <li v-for="(error, index) in validationErrors" :key="index">{{ error.message }}</li>
                </ul>
              </div>
            </div>
            <template v-if="showHistory">
              <field-history-content object-type="FormSubmission" :object-id="submission.id.toString()"></field-history-content>
            </template>
            <template v-else-if="viewOnly">
              <b-field v-for="item in submission.results" :label="item.key" :key="item.key">
                <p class="submission__value">{{ Array.isArray(item.value) ? item.value.join(', ') : item.value }}</p>
              </b-field>
            </template>
            <template v-else>
              <template v-for="(field, index) in selectedForm.fields">
                <!-- eslint-disable-next-line vue/valid-v-for -->
                <component :is="`${field.type}-element-renderer`" v-model="filledValues[index].value" v-bind="field"></component>
              </template>

              <b-field label="" v-if="selectedForm.can_update">
                <b-checkbox v-model="isCompleted" :disabled="status === 'completed' || status === 'needs_review' || validationErrors.length > 0">Completed</b-checkbox>
              </b-field>
            </template>

          </div>
        </template>
      </template>
      <template #footer v-if="!showHistory && !viewOnly">
        <div class="action-buttons">
          <button class="button is-white" @click="$emit('close')">Cancel</button>
          <button class="button is-primary" v-if="selectedFormId" :disabled="!meta.valid" @click="handleSubmit(save)">Save</button>
        </div>
      </template>
    </ld-modal-content>
    <aimee-conversation-modal
      v-model="isAimeeModalActive"
      :conversation-id="aimeeConversationId" />
  </validation-observer>
</template>

<script>
import to from "@/lib/to"
import RendererComponents from "@/components/form_builder/RendererComponents"
import LdModalContent from "@/components/LdModalContent"
import ValidationObserver from "@/components/validation/ValidationObserver"
import FieldHistoryContent from "@/components/FieldHistoryContent"
import AimeeConversationModal from "@/components/aimee/AimeeConversationModal"


export default {
  name: "FormFill",
  components: {ValidationObserver, LdModalContent, FieldHistoryContent, AimeeConversationModal, ...RendererComponents},
  props: ['patientId', 'patientMonitorId', 'submission', 'viewOnly'],
  async mounted() {
    const [err, response] = await to(this.http.get('/forms/dropdown'))
    if (!err) {
      this.forms = response.data.filter( x => x.is_active)
    }

    if (this.submission) {
      this.isEditing = true
      this.selectedFormId = this.submission.form.id
      this.formSelected({ target: { value: this.submission.form.id } })

      this.$nextTick(() => {
        if (Array.isArray(this.submission.results)) {
          this.submission.results.forEach((result) => {
            const fieldIndex = this.filledValues.findIndex(f => f.key === result.key)
            if (fieldIndex !== -1) {
              this.filledValues[fieldIndex].value = result.value
            }
          })
          this.status = this.submission.status
        }
      })

      if (this.submission.status === 'needs_review') {
        this.fetchValidationErrors()
      }
    }
  },
  methods: {
    truncateDescription(description) {
      return this.isTruncated(description) ? description.substring(0, 50) + '...' : description
    },
    isTruncated(description) {
      return description && description.length > 50
    },
    formSelected({ target: { value: formId } }) {
      if (!formId) return;
      const form = this.forms.filter(x => x.id == formId)[0]
      this.filledValues = []
      form.fields.forEach(field => {
        this.filledValues.push({
          key: field.label,
          value: field.defaultValue
        })
      })
      this.selectedForm = form
    },
    async fetchValidationErrors() {
      if (!this.submission?.id) return
      const [err, response] = await to(this.http.post(`/form_submissions/${this.submission.id}/validate`))
      if (!err) {
        this.validationErrors = response.data.errors || []
      }
    },
    async save() {
      const payload = {
        patient_monitor_id: parseInt(this.patientMonitorId) || null,
        patient_id: parseInt(this.patientId),
        form_id: this.selectedFormId,
        results: this.filledValues,
        status: this.isCompleted ? 'completed' : 'in_progress'
      }

      let err, response
      if (this.isEditing && this.submission) {
        [err, response] = await to(this.http.put(`/form_submissions/${this.submission.id}`, payload))
      } else {
        [err, response] = await to(this.http.post('/form_submissions', payload))
      }

      if (!err) {
        const updatedSubmission = response.data

        if (updatedSubmission.validation_errors?.length > 0) {
          this.validationErrors = updatedSubmission.validation_errors
          return
        }

        this.validationErrors = []
        this.$emit('close')
        this.$emit('save')
        this.$buefy.toast.open({
          message: this.isEditing ? 'Action successfully updated' : 'Action successfully submitted',
          type: 'is-success'
        })
      }
    },
    toggleHistory() {
      this.showHistory = !this.showHistory
    },
    openAimeeConversation() {
      if (this.submission?.aimee_conversation_id) {
        this.aimeeConversationId = this.submission.aimee_conversation_id
        this.isAimeeModalActive = true
      } else {
        this.$buefy.toast.open({
          message: 'No Aimee conversation found for this submission',
          type: 'is-warning'
        })
      }
    }
  },
  data() {
    return {
      forms: [],
      selectedFormId: null,
      selectedForm: {},
      filledValues: [],
      isEditing: false,
      status: 'in_progress',
      isCompleted: false,
      showHistory: false,
      isAimeeModalActive: false,
      aimeeConversationId: null,
      validationErrors: []
    }
  }
}
</script>

<style scoped lang="scss">
  .form-fill-container {
    container-type: inline-size;
    container-name: form-fill;
  }

  .choose-form h1 {
    font-size: 23px;
    font-weight: 600;
    line-height: 26px;
    margin-bottom: 20px;
    text-align: center;
  }

  .form-modal {
    max-width: 600px;
    overflow: visible;
    :deep(.ld-modal__header) {
      border-radius: 8px 8px 0 0;
    }
  }

  @container form-fill (max-width: 500px) {
    .form-modal :deep(.ld-modal__header-left button span:not(.icon)) {
      display: none;
    }
  }

  .choose-form__title {
    font-size: 14px;
    color: var(--ld-text-primary);
    white-space: nowrap;
  }
  .action-buttons {
    margin: 10px 0 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    > button:first-child {
      margin-right: 10px;
    }
  }
  .form-content {
    overflow-y: auto;
  }
  .description-button {
    width: 26px;
    height: 26px;
    padding: 0;
    i {
      font-size: 17px;
    }
  }
  .form-description {
    white-space: pre-line;
    padding: 10px;
    max-width: 380px;
    max-height: 200px;
    overflow-y: auto;
  }
  .form-tabs {
    margin: 0 -20px;
    :deep(.tabs ul) {
      padding: 0 11px;
    }
    :deep(.tab-content) {
      display: block !important;
      padding: 10px 20px;
    }
  }
  .submission__value {
    white-space: pre-line;
    line-height: 20px;
    font-size: 15px;
  }
  .validation-errors {
    margin-bottom: 16px;
    .notification {
      padding: 12px 16px;
    }
    ul {
      margin-left: 16px;
      margin-top: 4px;
    }
    li {
      list-style-type: disc;
      font-size: 14px;
      line-height: 1.5;
    }
  }
</style>

<style lang="scss">
body:has(.aimee-conversation-modal.is-active) .modal:has(.form-fill-container) > .modal-content > .modal-close {
  display: none !important
}
</style>
